import { useEffect } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import {
  HubOption,
  Priority,
  ServiceTicket,
  ServiceTicketPayload,
  ServiceType,
  Technician,
  VehicleOption,
} from "../types";

interface ServiceTicketFormDialogProps {
  open: boolean;
  loading?: boolean;
  vehiclesLoading?: boolean;
  ticket?: ServiceTicket | null;
  hubs: HubOption[];
  vehicles: VehicleOption[];
  inspectors?: Technician[];
  inspectorsLoading?: boolean;
  defaultServiceType?: ServiceType;
  onClose: () => void;
  onHubChange?: (hubId: string) => void;
  onInspectorHubChange?: (hubId: string) => void;
  onSubmit: (payload: ServiceTicketPayload) => Promise<void>;
}

const defaultValues: ServiceTicketPayload = {
  vehicleId: "",
  hubId: "",
  serviceType: "ON_ROAD",
  priority: "MEDIUM",
  issueTitle: "",
  issueDescription: "",
  remarks: "",
};

const ServiceTicketFormDialog = ({
  open,
  loading = false,
  vehiclesLoading = false,
  ticket,
  hubs,
  vehicles,
  inspectors = [],
  inspectorsLoading = false,
  defaultServiceType = "ON_ROAD",
  onClose,
  onHubChange,
  onInspectorHubChange,
  onSubmit,
}: ServiceTicketFormDialogProps) => {
  const { control, handleSubmit, reset, setValue, watch } = useForm<ServiceTicketPayload>({
    defaultValues,
  });
  const selectedHubId = watch("hubId");
  const serviceType = watch("serviceType");
  const selectedVehicleId = watch("vehicleId");
  const selectedVehicle = vehicles.find(
    (vehicle) =>
      vehicle.registrationNumber === selectedVehicleId ||
      vehicle.id === selectedVehicleId
  );
  const isPreventive = serviceType === "PREVENTIVE";
  const isInHouse = serviceType === "IN_HOUSE";
  const dialogTitle = ticket
    ? "Edit Service Ticket"
    : isPreventive
      ? "Create Preventive Schedule"
      : isInHouse
        ? "Create Workshop Ticket"
        : "Raise Service Ticket";
  const submitLabel = loading
    ? "Saving..."
    : ticket
      ? "Update Ticket"
      : isPreventive
        ? "Create Schedule"
        : isInHouse
          ? "Create Workshop Ticket"
          : "Raise Ticket";
  const filteredVehicles = vehicles.filter((vehicle) => {
    if (!selectedHubId) return false;
    return vehicle.hubId === selectedHubId;
  });

  useEffect(() => {
    if (ticket) {
      reset({
        vehicleId: ticket.vehicleId,
        hubId: ticket.warehouseId,
        serviceType: ticket.serviceType,
        priority: ticket.priority,
        issueTitle: ticket.issueTitle,
        issueDescription: ticket.issueDescription,
        remarks: ticket.remarks || "",
      });
    } else {
      reset({
        ...defaultValues,
        serviceType: defaultServiceType,
        issueTitle:
          defaultServiceType === "PREVENTIVE"
            ? "Scheduled Preventive Maintenance"
            : defaultServiceType === "IN_HOUSE"
              ? "Workshop Inspection"
              : "",
      });
    }
  }, [defaultServiceType, ticket, reset]);

  useEffect(() => {
    if (open && selectedHubId) {
      onHubChange?.(selectedHubId);
      if (isInHouse) {
        onInspectorHubChange?.(selectedHubId);
      }
    }
  }, [isInHouse, onHubChange, onInspectorHubChange, open, selectedHubId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="hubId"
                control={control}
                rules={{ required: "Hub is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    required
                    label="Hub"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      (hubs.length ? "" : "Vehicle Service hubs are not loaded.")
                    }
                    onChange={(event) => {
                      field.onChange(event);
                      setValue("vehicleId", "");
                      setValue("inspectorId", "");
                    }}
                  >
                    {hubs.map((hub) => (
                      <MenuItem key={hub.id} value={hub.id}>
                        {hub.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="vehicleId"
                control={control}
                rules={{ required: "Vehicle is required" }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={filteredVehicles}
                    disabled={!selectedHubId || vehiclesLoading}
                    value={
                      filteredVehicles.find(
                        (vehicle) =>
                          vehicle.registrationNumber === field.value ||
                          vehicle.id === field.value
                      ) ||
                      (field.value
                        ? {
                            id: field.value,
                            registrationNumber: field.value,
                            label: field.value,
                          }
                        : null)
                    }
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id ||
                      option.registrationNumber === value.registrationNumber
                    }
                    onChange={(_, vehicle) =>
                      field.onChange(vehicle?.registrationNumber || "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Vehicle"
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ||
                          (!selectedHubId
                            ? "Select hub first."
                            : vehiclesLoading
                              ? "Loading vehicles for selected hub..."
                            : filteredVehicles.length
                              ? ""
                              : "No vehicles found for this hub.")
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="serviceType"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Service Type"
                    disabled={!ticket}
                  >
                    <MenuItem value="ON_ROAD">On Road</MenuItem>
                    <MenuItem value="PREVENTIVE">Preventive</MenuItem>
                    <MenuItem value="IN_HOUSE">In House</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            {(isPreventive || isInHouse) && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Chassis"
                  value={selectedVehicle?.chassisNumber || ticket?.chassisNumber || ""}
                  InputProps={{ readOnly: true }}
                  helperText={
                    selectedVehicle || ticket?.chassisNumber
                      ? ""
                      : "Select a vehicle to auto-fill chassis."
                  }
                />
              </Grid>
            )}

            {isPreventive && (
              <>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="currentOdometer"
                    control={control}
                    rules={{ required: "Current odometer is required" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        type="number"
                        label="Current Odometer (KM)"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        inputProps={{ min: 0 }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="serviceInterval"
                    control={control}
                    rules={{ required: "Service interval is required" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        required
                        label="Service Interval"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        <MenuItem value="30_DAYS">30 Days</MenuItem>
                        <MenuItem value="45_DAYS">45 Days</MenuItem>
                        <MenuItem value="60_DAYS">60 Days</MenuItem>
                        <MenuItem value="MILEAGE">Mileage Based</MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="dueDate"
                    control={control}
                    rules={{ required: "Due date is required" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        type="date"
                        label="Due Date"
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {isInHouse && (
              <>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="inspectorId"
                    control={control}
                    rules={{ required: "Inspector is required" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        required
                        label="Inspector"
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ||
                          (!selectedHubId
                            ? "Select a hub to view eligible inspectors."
                            : inspectorsLoading
                              ? "Loading eligible AMEs and technicians..."
                              : inspectors.length
                                ? ""
                                : "Assign AME or Technician users for this hub's city in Maintenance Assignments.")
                        }
                      >
                        {inspectors.map((inspector) => (
                          <MenuItem key={inspector.id} value={inspector.id}>
                            {inspector.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="issueCategory"
                    control={control}
                    rules={{ required: "Issue category is required" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        required
                        label="Issue Category"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        <MenuItem value="MECHANICAL">Mechanical</MenuItem>
                        <MenuItem value="ELECTRICAL">Electrical</MenuItem>
                        <MenuItem value="BATTERY">Battery</MenuItem>
                        <MenuItem value="BODY_WORK">Body Work</MenuItem>
                        <MenuItem value="TYRE_BRAKE">Tyre / Brake</MenuItem>
                        <MenuItem value="OTHER">Other</MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={6}>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select fullWidth label="Priority">
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="CRITICAL">Critical</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="issueTitle"
                control={control}
                rules={{ required: "Issue title is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={
                      isPreventive
                        ? "Schedule Title"
                        : isInHouse
                          ? "Workshop Issue"
                          : "Issue Title"
                    }
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="issueDescription"
                control={control}
                rules={{ required: "Issue description is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    label={
                      isPreventive
                        ? "Inspection Scope"
                        : isInHouse
                          ? "Workshop Remarks"
                          : "Issue Description"
                    }
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth multiline minRows={2} label="Remarks" />
                )}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit((values) => {
            const metadata: string[] = [];

            if (isPreventive) {
              metadata.push(`Current Odometer: ${values.currentOdometer || 0} KM`);
              metadata.push(`Service Interval: ${values.serviceInterval || "-"}`);
              metadata.push(`Due Date: ${values.dueDate || "-"}`);
            }

            if (isInHouse) {
              metadata.push(`Inspector: ${values.inspectorId || "-"}`);
              metadata.push(`Issue Category: ${values.issueCategory || "-"}`);
            }

            return onSubmit({
              ...values,
              priority: values.priority as Priority,
              chassisNumber: selectedVehicle?.chassisNumber || values.chassisNumber,
              remarks: [values.remarks, ...metadata].filter(Boolean).join("\n"),
            });
          })}
          disabled={loading}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceTicketFormDialog;
